<script setup lang="ts">
/**
 * Under a customer's picture: "ตรวจสลิป" (staff click) → result badge. Staff-only: nothing is sent to the
 * customer. A final result is never checked again (each check costs money); pending/error can be retried.
 * A slip that passed while the chat has an order waiting for the transfer ("ยังไม่โอน · ส่ง QR ให้ลูกค้า", the POS held
 * bill — api/app/pos_pending.py): "ยืนยันชำระ" with the slip's time and amount, one click (an amount that is not the
 * order total shows the difference and needs a second click "ยืนยันทั้งที่ยอดไม่ตรง").
 */
import { computed, ref, watch } from 'vue'
import type { ChatMessage, SlipCheck } from '../../api/types'
import { baht, clockTime, dayKey, shortDate } from '../../lib/inbox'
import { payPrefill } from '../../lib/order'
import { useClock } from '../../stores/clock'
import { useInbox } from '../../stores/inbox'
import { useOrders } from '../../stores/order'
import { useSlips } from '../../stores/slips'
import Icon from '../ui/Icon.vue'

const props = defineProps<{ item: ChatMessage }>()
const slips = useSlips()
const clock = useClock()
const orders = useOrders()
const inbox = useInbox()

const slip = computed<SlipCheck | null>(() => props.item.slip ?? null)
// "checking" from another screen (or the auto-check); a check that died mid-way comes back with can_retry
const busy = computed(() => !!slips.busy[props.item.id] || (slip.value?.status === 'checking' && !slip.value.can_retry))

type Tone = 'ok' | 'warn' | 'crit' | 'neutral'
const TONE: Record<string, Tone> = {
  ok: 'ok',
  review: 'warn',
  pending: 'warn',
  duplicate: 'crit',
  wrong_receiver: 'crit',
  not_slip: 'neutral',
  error: 'neutral',
}
const ICON: Record<Tone, 'checkCircle' | 'alertCircle' | 'info'> = {
  ok: 'checkCircle',
  warn: 'alertCircle',
  crit: 'alertCircle',
  neutral: 'info',
}
const tone = computed<Tone>(() => TONE[slip.value?.status ?? ''] ?? 'neutral')

/** "14:02" today, "25 ก.ย. 14:02" otherwise (Bangkok time). */
function when(iso: string | null): string | null {
  if (!iso) return null
  const t = clockTime(iso)
  return dayKey(iso) === dayKey(clock.now) ? t : `${shortDate(iso)} ${t}`
}

/** One calm line: what happened + the numbers staff need. */
const line = computed(() => {
  const s = slip.value
  if (!s) return ''
  const parts: string[] = []
  if (s.amount !== null && ['ok', 'review', 'duplicate', 'wrong_receiver'].includes(s.status)) parts.push(baht(s.amount))
  if (s.status === 'ok' || s.status === 'review') {
    const bw = [s.bank, when(s.slip_at)].filter(Boolean).join(' ')
    if (bw) parts.push(bw)
  }
  return parts.join(' · ')
})

/** Second line for the results that need a reason (duplicate: where it was first seen). */
const detail = computed(() => {
  const s = slip.value
  if (!s) return null
  if (s.status === 'duplicate' && s.dup) {
    const d = [s.dup.at ? `ครั้งแรก ${s.dup.at}` : null, s.dup.where, s.dup.by ? `โดย ${s.dup.by}` : null].filter(Boolean)
    return d.length ? d.join(' · ') : null
  }
  if (['review', 'wrong_receiver', 'pending', 'error'].includes(s.status)) return s.note
  return null
})

const title = computed(() => {
  const s = slip.value
  if (!s) return ''
  const bits = [s.trans_ref ? `เลขอ้างอิง ${s.trans_ref}` : null, s.checked_at ? `ตรวจเมื่อ ${when(s.checked_at)}` : null,
    s.via === 'auto' ? 'ตรวจอัตโนมัติ' : null]
  return bits.filter(Boolean).join(' · ')
})

function run(): void {
  void slips.check(props.item.conversation_id, props.item.id)
}

// ------------------------------------------------ "ยืนยันชำระ" of an order waiting for the transfer
const conv = computed(() => props.item.conversation_id)
/** Passed (or needs a look) with a transfer time and amount: may confirm a held order. */
const payable = computed(() => {
  const s = slip.value
  return !!s && ['ok', 'review'].includes(s.status) && !!s.slip_at && s.amount !== null
})
watch(payable, (ok) => ok && void orders.ensureContext(conv.value), { immediate: true })
/** The order this slip already confirmed. */
const usedBy = computed(() => (orders.contexts[conv.value]?.chat_orders ?? []).find((o) => o.paid?.slip_message_id === props.item.id) ?? null)
/** The held order to confirm: the one whose total is the slip amount, else the newest. */
const target = computed(() => {
  const held = orders.heldOrders(conv.value)
  const amt = slip.value?.amount
  if (!held.length || amt === null || amt === undefined) return null
  return held.find((o) => o.total !== null && Math.abs(o.total - amt) < 0.001) ?? held[0] ?? null
})
const prefill = computed(() => (target.value && slip.value ? payPrefill(slip.value, target.value.total, props.item.id) : null))
const armed = ref(false)
const payErr = ref<string | null>(null)
const paying = computed(() => !!(target.value && orders.payBusy[target.value.id]))
watch(target, () => {
  armed.value = false
  payErr.value = null
})
async function confirmPay(): Promise<void> {
  const t = target.value
  const p = prefill.value
  if (!t || !p || paying.value) return
  payErr.value = null
  if (p.diff !== 0 && !armed.value) {
    armed.value = true
    return
  }
  const out = await orders.confirmPay(conv.value, t.id, {
    paid_at: p.paid_at,
    amount: p.amount,
    slip_message_id: props.item.id,
    accept_diff: armed.value && p.diff !== 0,
  })
  if (out.kind === 'done') {
    armed.value = false
    inbox.flash(`ยืนยันชำระแล้ว ออกใบ ${out.order.order_code ?? ''} ใน POS`.trim())
  } else if (out.kind === 'mismatch') {
    armed.value = true
    payErr.value = out.message
  } else {
    payErr.value = out.message
  }
}
</script>

<template>
  <div class="slip" aria-live="polite">
    <span v-if="busy" class="sl-busy"><span class="sl-dot" aria-hidden="true"></span>กำลังตรวจสลิป…</span>
    <template v-else-if="slip">
      <span class="sl-badge" :class="`t-${tone}`" :title="title">
        <Icon :name="ICON[tone]" />
        <b>{{ slip.label }}</b>
        <span v-if="line" class="sl-v mono">{{ line }}</span>
      </span>
      <span v-if="detail" class="sl-note" :class="`n-${tone}`">{{ detail }}</span>
      <button v-if="slip.can_retry" type="button" class="sl-btn" @click="run">
        <Icon name="repeat" />ตรวจอีกครั้ง
      </button>
      <span v-if="payable && usedBy" class="sl-used"><Icon name="checkCircle" />ใช้ยืนยันชำระ {{ usedBy.order_code ?? 'ออเดอร์' }} แล้ว</span>
      <template v-else-if="payable && target && prefill">
        <button
          type="button"
          class="sl-btn sl-pay"
          :class="{ second: armed && prefill.diff !== 0 }"
          :disabled="paying"
          :title="`ยืนยันว่าลูกค้าโอนแล้ว ${prefill.paid_at} น. · POS ออกใบจากพักบิล ${target.hold?.code ?? ''}`"
          @click="confirmPay"
        >
          <span v-if="paying" class="sl-dot" aria-hidden="true"></span><Icon v-else name="checkCircle" />{{
            armed && prefill.diff !== 0 ? 'ยืนยันทั้งที่ยอดไม่ตรง' : `ยืนยันชำระ ${baht(target.total ?? 0)}`
          }}
        </button>
        <span v-if="prefill.diff !== 0" class="sl-note n-warn"
          >ยอดสลิป{{ prefill.diff > 0 ? 'เกิน' : 'ขาด' }}ยอดที่ต้องชำระ {{ baht(Math.abs(prefill.diff)) }}</span
        >
        <span v-if="payErr" class="sl-note n-crit">{{ payErr }}</span>
      </template>
    </template>
    <button v-else type="button" class="sl-btn" title="ตรวจยอดและบัญชีผู้รับ ลูกค้าไม่เห็นผลนี้" @click="run">
      <Icon name="receipt" />ตรวจสลิป
    </button>
  </div>
</template>

<style scoped>
.slip {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  max-width: 100%;
  padding: 0 2px;
}
.sl-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 26px;
  padding: 0 10px 0 8px;
  border: 1px solid var(--line);
  border-radius: 999px;
  background: #fff;
  color: var(--ac2);
  font-size: 12.5px;
  font-weight: 600;
  line-height: 19px;
  white-space: nowrap;
}
.sl-btn:hover {
  background: var(--acs);
  border-color: #bfdccd;
}
.sl-btn :deep(.ic) {
  width: 14px;
  height: 14px;
}
.sl-busy {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 26px;
  font-size: 12.5px;
  line-height: 19px;
  color: var(--mut);
  white-space: nowrap;
}
.sl-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--ac);
  animation: sl-pulse 1s ease-in-out infinite;
}
@keyframes sl-pulse {
  50% {
    opacity: 0.25;
  }
}
.sl-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 26px;
  padding: 3px 10px 3px 8px;
  border-radius: 999px;
  font-size: 12.5px;
  line-height: 19px;
  max-width: 100%;
}
.sl-badge b {
  font-weight: 600;
  white-space: nowrap;
}
.sl-badge :deep(.ic) {
  width: 14px;
  height: 14px;
}
.sl-v {
  font-size: 12.5px;
  font-weight: 500;
}
.t-ok {
  background: var(--acs);
  color: var(--ac2);
}
.t-warn {
  background: var(--warns);
  color: var(--warn);
}
.t-crit {
  background: var(--crits);
  color: #a61b12;
}
.t-neutral {
  background: #eef2f0;
  color: #3e4d46;
}
.sl-note {
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
  white-space: normal;
  overflow-wrap: anywhere;
}
.sl-note.n-crit {
  color: #a61b12;
}
.sl-note.n-warn {
  color: var(--warn);
}
.sl-pay {
  border-color: #bfdccd;
  background: var(--acs);
}
.sl-pay.second {
  border-color: #e7c27a;
  background: var(--warns);
  color: #7a4700;
}
.sl-pay:disabled {
  opacity: 0.7;
}
.sl-used {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  line-height: 18px;
  color: var(--ac2);
}
.sl-used :deep(.ic) {
  width: 13px;
  height: 13px;
}
</style>
